import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, Send, FileSignature, AlertTriangle, Download, Receipt } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Toolbar } from '@/components/Toolbar'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { api } from '@/lib/api'
import { tokenManager } from '@/lib/tokenManager'
import { formatPayrollMoney } from '@/features/payroll/format'
import { formatDate } from '@/lib/locale'
import type { TaxFiling } from '@/features/tax/extras'
import { SchedulePaymentModal } from '@/features/tax/TaxModals'

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'secondary' | 'outline' | 'danger' | 'info'> = {
  draft: 'outline', generated: 'info', reviewed: 'info', signed: 'info',
  submitted: 'info', acknowledged: 'info', filed: 'success',
  amended: 'warning', rejected: 'danger', expired: 'secondary',
}

export function FilingDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { hasPermission, user } = useAuth()
  const { toast } = useToast()
  const qc = useQueryClient()
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['tenant', user?.tenantId ?? 'anon', 'tax', 'filing', id],
    queryFn: () => api.get<{ data: TaxFiling & { payload: Record<string, unknown> } }>(`/api/tax/filings/${id}`),
    enabled: Boolean(user?.tenantId) && Boolean(id),
  })
  const filing = data?.data
  const [busy, setBusy] = useState<'sign' | 'submit' | 'amend' | null>(null)
  const [signAck, setSignAck] = useState(false)
  const [payOpen, setPayOpen] = useState(false)

  async function downloadPdf() {
    if (!id) return
    try {
      const token = tokenManager.get()
      const res = await fetch(`/api/tax/filings/${id}/pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      if (!res.ok) throw new Error(`Failed to download (${res.status})`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${filing?.filing_type || 'filing'}-${filing?.tax_year || ''}.pdf`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      toast({ title: 'Download failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    }
  }

  async function call(action: 'sign' | 'submit' | 'amend') {
    if (!id) return
    setBusy(action)
    try {
      const r = await api.post<{ data: { status: string } }>(`/api/tax/filings/${id}/${action}`, {})
      toast({ title: `Filing ${action}ed`, description: `Status: ${r.data.status}` })
      if (user?.tenantId) {
        qc.invalidateQueries({ queryKey: ['tenant', user.tenantId, 'tax', 'filing', id] })
        qc.invalidateQueries({ queryKey: ['tenant', user.tenantId, 'tax', 'filings'] })
        qc.invalidateQueries({ queryKey: ['tenant', user.tenantId, 'tax', 'dashboard'] })
      }
      await refetch()
    } catch (err) {
      toast({
        title: `${action} failed`,
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      })
    } finally {
      setBusy(null)
    }
  }

  if (isLoading || !filing) {
    return <Page title="Tax filing"><div className="flex justify-center py-12"><LoadingSpinner /></div></Page>
  }

  const canReview = hasPermission('tax.filings.review') && filing.status === 'generated'
  const canSign = hasPermission('tax.filings.sign') && ['generated', 'reviewed'].includes(filing.status)
  const canSubmit = hasPermission('tax.filings.file') && filing.status === 'signed'
  const canAmend = hasPermission('tax.filings.amend') && ['filed', 'acknowledged'].includes(filing.status)
  const canSchedulePay = hasPermission('tax.payments.schedule') && BigInt(filing.total_due_minor_units || '0') > 0n

  return (
    <Page
      title={`Filing ${filing.filing_type.toUpperCase().replace(/_/g, ' ')}`}
      description={`Tax year ${filing.tax_year} · Period ${formatDate(filing.period_start)} → ${formatDate(filing.period_end)} · Due ${formatDate(filing.due_date)}`}
      actions={
        <Toolbar>
          <Button variant="outline" onClick={() => navigate('/tax/filings')}>
            <ArrowLeft className="mr-2 size-4" /> Back
          </Button>
          <Button variant="secondary" onClick={downloadPdf}>
            <Download className="mr-2 size-4" /> PDF
          </Button>
          {canSchedulePay ? (
            <Button variant="secondary" onClick={() => setPayOpen(true)}>
              <Receipt className="mr-2 size-4" /> Schedule payment
            </Button>
          ) : null}
          {canSign ? (
            <Button onClick={() => call('sign')} disabled={busy === 'sign' || !signAck}>
              <FileSignature className="mr-2 size-4" /> {busy === 'sign' ? 'Signing…' : 'Sign'}
            </Button>
          ) : null}
          {canSubmit ? (
            <Button onClick={() => call('submit')} disabled={busy === 'submit'}>
              <Send className="mr-2 size-4" /> {busy === 'submit' ? 'Submitting…' : 'Submit to authority'}
            </Button>
          ) : null}
          {canAmend ? (
            <Button variant="outline" onClick={() => call('amend')} disabled={busy === 'amend'}>
              {busy === 'amend' ? 'Amending…' : 'File amendment'}
            </Button>
          ) : null}
        </Toolbar>
      }
    >
      <Card>
        <CardContent className="grid gap-4 p-6 sm:grid-cols-4">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Status</div>
            <Badge variant={STATUS_VARIANT[filing.status] || 'secondary'} className="mt-1">{filing.status}</Badge>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Total due</div>
            <div className="mt-1 text-lg font-semibold">{formatPayrollMoney(filing.total_due_minor_units, filing.currency)}</div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Total paid</div>
            <div className="mt-1 text-lg font-semibold text-emerald-600">{formatPayrollMoney(filing.total_paid_minor_units, filing.currency)}</div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Filed</div>
            <div className="mt-1 text-sm">{filing.filed_at ? formatDate(filing.filed_at) : '-'}</div>
          </div>
        </CardContent>
      </Card>

      {canReview ? (
        <Card>
          <CardContent className="space-y-3 p-5">
            <div className="flex items-center gap-2">
              <AlertTriangle className="size-4 text-amber-500" />
              <h2 className="text-sm font-semibold">Review before signing</h2>
            </div>
            <p className="text-xs text-muted-foreground">
              By signing, you certify that the information is true, correct, and complete. The legal responsibility for the filing rests with the signing taxpayer.
            </p>
            <div className="flex items-center gap-2 text-sm">
              <Checkbox id="filing-sign-ack" checked={signAck} onCheckedChange={(v) => setSignAck(v === true)} />
              <label htmlFor="filing-sign-ack">I have reviewed every line and accept the sign-off statement.</label>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="space-y-3 p-6">
          <h2 className="text-base font-semibold">Form payload</h2>
          <p className="text-xs text-muted-foreground">
            The generated form data as it will be transmitted. Every line is computed from your posted payroll / ledger / vendor data.
          </p>
          <pre className="overflow-x-auto rounded bg-muted/50 p-3 text-xs">{JSON.stringify(filing.payload, null, 2)}</pre>
        </CardContent>
      </Card>

      {filing.status === 'submitted' || filing.status === 'acknowledged' || filing.status === 'filed' ? (
        <Card>
          <CardContent className="space-y-2 p-5">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-5 text-emerald-500" />
              <h2 className="text-base font-semibold">Filed</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Acknowledgment ID: <code>{(filing as TaxFiling & { acknowledgment_id?: string }).acknowledgment_id ?? '-'}</code>
            </p>
            <p className="text-xs text-muted-foreground">
              Electronic transmission to the IRS (FIRE / MeF) and state portals activates once your e-file transmitter credentials are configured. This filing is recorded and tracked with the acknowledgment reference above.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {payOpen ? (
        <SchedulePaymentModal
          open={payOpen}
          onOpenChange={setPayOpen}
          filingId={filing.id}
          defaultAmountMinor={filing.total_due_minor_units}
        />
      ) : null}
    </Page>
  )
}
